/**
 * Attempts to extract and parse a JSON object from an agent response string.
 *
 * Handles common cases where the agent wraps the JSON in markdown code fences
 * or includes surrounding explanatory text.
 *
 * Returns null if no valid JSON can be found.
 */
export function extractJson<T = unknown>(text: string): T | null {
  // 1. Try to extract from markdown code fences: ```json ... ``` or ``` ... ```
  const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
  if (fenceMatch) {
    try {
      return JSON.parse(fenceMatch[1]) as T;
    } catch {
      // Fall through to other strategies
    }
  }

  // 2. Find the first { ... } block (outermost braces)
  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace > firstBrace) {
    try {
      return JSON.parse(text.slice(firstBrace, lastBrace + 1)) as T;
    } catch {
      // Fall through
    }
  }

  // 2b. Find the first [ ... ] block (outermost brackets – raw arrays)
  const firstBracket = text.indexOf('[');
  const lastBracket = text.lastIndexOf(']');
  if (firstBracket !== -1 && lastBracket > firstBracket) {
    try {
      return JSON.parse(text.slice(firstBracket, lastBracket + 1)) as T;
    } catch {
      // Fall through
    }
  }

  // 3. Try parsing the entire trimmed string
  try {
    return JSON.parse(text.trim()) as T;
  } catch {
    return null;
  }
}
